import React, { useEffect, useRef, useState } from 'react';
import './WorkoutStreamViewer.css';

export async function attachWorkoutStreamPlayer(videoElement, url, loadPlayerSdk = () => import('amazon-ivs-player'), options = {}) {
  if (!videoElement || !url) throw new Error('A video element and playback URL are required.');
  videoElement.crossOrigin = options.includeCredentials ? 'use-credentials' : 'anonymous';
  if (videoElement.canPlayType?.('application/vnd.apple.mpegurl')) {
    videoElement.src = url;
    await videoElement.play().catch(() => {});
    return { delete: () => { videoElement.removeAttribute('src'); videoElement.load(); } };
  }
  const sdk = await loadPlayerSdk();
  if (!sdk.isPlayerSupported) throw new Error('Live workout playback is not supported in this browser.');
  const player = sdk.create({
    wasmWorker: 'https://player.live-video.net/1.48.0/amazon-ivs-wasmworker.min.js',
    wasmBinary: 'https://player.live-video.net/1.48.0/amazon-ivs-wasmworker.min.wasm',
  });
  player.attachHTMLVideoElement(videoElement);
  if (options.includeCredentials) player.setRequestCredentials('include');
  player.load(url);
  player.play();
  return player;
}

const WorkoutStreamViewer = ({ activityId, stream, backendService }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [state, setState] = useState('idle');
  const [kind, setKind] = useState(null);
  const [error, setError] = useState(null);
  const available = ['ready', 'live'].includes(stream?.status)
    || stream?.recording?.status === 'ready';

  useEffect(() => () => {
    playerRef.current?.delete?.();
  }, []);

  if (!available) return null;

  const watch = async () => {
    setState('loading');
    setError(null);
    try {
      const playback = await backendService.getWorkoutStreamPlayback(activityId);
      playerRef.current?.delete?.();
      playerRef.current = await attachWorkoutStreamPlayer(
        videoRef.current,
        playback.url,
        undefined,
        { includeCredentials: playback.kind === 'recording' }
      );
      setKind(playback.kind);
      setState('playing');
    } catch (playbackError) {
      setError(playbackError?.response?.data?.error || playbackError.message || 'Workout video is unavailable.');
      setState('error');
    }
  };

  return (
    <section className="workout-stream-viewer" aria-label="Workout video stream">
      <div className="workout-stream-viewer-header">
        <div>
          <strong>{kind === 'recording' ? 'Workout recording' : 'Live workout'}</strong>
          <span>{stream.status === 'live' || stream.status === 'ready' ? 'Broadcasting now' : 'Saved from this workout'}</span>
        </div>
        {stream.status === 'live' || stream.status === 'ready' ? <span className="workout-stream-live-badge">LIVE</span> : null}
      </div>
      <video
        ref={videoRef}
        className={state === 'playing' ? 'is-playing' : ''}
        controls
        playsInline
        crossOrigin="anonymous"
        aria-label="Workout live stream player"
      />
      {state !== 'playing' && (
        <button type="button" className="workout-stream-watch" onClick={watch} disabled={state === 'loading'}>
          {state === 'loading' ? 'Authorizing…' : stream.status === 'live' || stream.status === 'ready' ? 'Watch live' : 'Play recording'}
        </button>
      )}
      {error && <p className="workout-stream-viewer-error" role="alert">{error}</p>}
    </section>
  );
};

export default WorkoutStreamViewer;
